/**
 * The remote desktop: noVNC's RFB client drawing into a box that fills the
 * dialog. The token goes as a WebSocket subprotocol (`["rs.v1", token]`),
 * never in the URL, so it stays out of access logs.
 *
 *  - Scaled to fit (`scaleViewport`); the remote desktop keeps its size.
 *  - Keyboard: noVNC captures keys while its canvas has focus; it takes focus
 *    on connect and on click. The dialog ignores Escape so a key meant for
 *    the device doesn't close the session.
 *  - Clipboard: not wired. We never listen for the desktop's clipboard and
 *    only send text when the user pastes through the staff-only control.
 */
import React from "react";
import { loadRfb, RfbClient, rfbProtocols } from "./rfbClient";
import { DeviceSessionCredentials } from "./useDeviceSession";

export const DeviceSessionScreen = ({
  credentials,
  onConnected,
  onDisconnected,
  onClient,
}: {
  credentials: DeviceSessionCredentials;
  onConnected: () => void;
  onDisconnected: () => void;
  /** The live client (null when gone), for paste-in. */
  onClient?: (client: RfbClient | null) => void;
}) => {
  const box = React.useRef<HTMLDivElement | null>(null);
  // Callbacks change identity on every render; the connection must not.
  const handlers = React.useRef({ onConnected, onDisconnected, onClient });
  handlers.current = { onConnected, onDisconnected, onClient };

  React.useEffect(() => {
    let client: RfbClient | null = null;
    let closed = false;
    const onConnect = () => {
      client?.focus();
      handlers.current.onConnected();
    };
    const onDisconnect = () => {
      handlers.current.onClient?.(null);
      if (!closed) handlers.current.onDisconnected();
    };
    loadRfb()
      .then((RFB) => {
        if (closed || !box.current) return;
        client = new RFB(box.current, credentials.wsUrl, {
          wsProtocols: rfbProtocols(credentials.token),
        });
        client.scaleViewport = true;
        client.resizeSession = false;
        client.clipViewport = false;
        client.focusOnClick = true;
        client.background = "#0b1220";
        client.addEventListener("connect", onConnect);
        client.addEventListener("disconnect", onDisconnect);
        handlers.current.onClient?.(client);
      })
      .catch(() => {
        if (!closed) handlers.current.onDisconnected();
      });
    return () => {
      closed = true;
      if (client) {
        client.removeEventListener("connect", onConnect);
        client.removeEventListener("disconnect", onDisconnect);
        try {
          client.disconnect();
        } catch {
          // Already closed.
        }
      }
      handlers.current.onClient?.(null);
    };
  }, [credentials.wsUrl, credentials.token]);

  return (
    <div
      ref={box}
      className="w-full h-full bg-[#0b1220] overflow-hidden"
      data-testid="device-session-screen"
    />
  );
};
